import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, TextInput, TouchableOpacity } from 'react-native';
import Typography from 'src/components/Text/Text';
import { themes } from 'src/theme/theme';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import { useGetTopCarriersMutation } from 'src/features/carrier/carrierEngineApi';
import {
  useUpdateCompanyNotesMutation,
  useGetCompanyNotesQuery,
} from 'src/features/company/api/companyApi';
import { formatDate, toDate } from 'src/helpers/dateHelper';

interface SummaryPanelProps {
  width: number;
  showProducerTools?: boolean;
}

const formatAmount = (amount: number | undefined): string => {
  if (!amount) return '';
  return `$${amount.toLocaleString()}`;
};

const getRankBadgeStyle = (rankLabel: string) => {
  const lower = rankLabel?.toLowerCase() || '';
  if (lower.includes('excellent') || lower.includes('very good')) {
    return { badge: styles.rankBadgeGreen, text: { color: themes.light.green[2] } };
  }
  if (lower.includes('good')) {
    return { badge: styles.rankBadgeBlue, text: { color: themes.light.brand_blue[3] } };
  }
  return { badge: styles.rankBadgeDefault, text: { color: themes.light.gray[5] } };
};

const SummaryPanel: React.FC<SummaryPanelProps> = ({ width, showProducerTools = false }) => {
  const company = useSelector((state: RootState) => state.company.company);
  const application = useSelector((state: RootState) => state.application.application);
  const filledTabsIndex = useSelector((state: RootState) => state.UI.filledTabsIndex);
  const producerFormId = useSelector((state: RootState) => state.users.producerFormId);
  const vehicles = useSelector((state: RootState) => state.vehicles.vehicles);
  const drivers = useSelector((state: RootState) => state.drivers.drivers);
  const freights = useSelector((state: RootState) => state.freights.companyCommodities);
  const loss = useSelector((state: RootState) => state.loss);

  const totalSteps = 6;
  const progressPercent = Math.round((filledTabsIndex / totalSteps) * 100);

  // Carriers
  const [getTopCarriers, { data: carriers, error: carrierError }] = useGetTopCarriersMutation();

  useEffect(() => {
    if (showProducerTools && company?.id) {
      getTopCarriers({ companyId: company.id });
    }
  }, [showProducerTools, company?.id, vehicles, drivers, freights, application, loss]);

  // Notes
  const [notes, setNotes] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [updateCompanyNotes] = useUpdateCompanyNotesMutation();
  const { data: savedNotes } = useGetCompanyNotesQuery(company?.id?.toString() || '', {
    skip: !company?.id || !showProducerTools,
  });

  useEffect(() => {
    if (savedNotes !== undefined) {
      setNotes(savedNotes || '');
    }
  }, [savedNotes]);

  const handleSaveNotes = async () => {
    if (!company?.id) return;
    try {
      setSaveStatus('saving');
      await updateCompanyNotes({
        companyId: company.id.toString(),
        note: notes,
        producerFormId: producerFormId || '',
      }).unwrap();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('idle');
    }
  };

  const isDirty = notes !== (savedNotes || '');

  // Build coverage list
  const coverages: { label: string; value: string }[] = [];
  if (application?.autoLiabilityRequested) {
    coverages.push({
      label: 'Auto Liability',
      value: formatAmount(application.autoLiabilityLimitRequested?.amount),
    });
  }
  if (application?.automobilePhysicalDamageRequested) {
    coverages.push({
      label: 'Physical Damage',
      value: formatAmount(application.deductible?.amount),
    });
  }
  if (application?.motorTruckCargoRequested) {
    coverages.push({
      label: 'Motor Truck Cargo',
      value: formatAmount(application.motorTruckCargoLimitRequested?.amount),
    });
  }
  if (application?.truckingGeneralInsuranceRequested) {
    coverages.push({
      label: 'General Liability',
      value: formatAmount(application.truckingGeneralInsuranceLimitRequested?.amount),
    });
  }
  if (application?.nonTruckingLiabilityRequested) {
    coverages.push({ label: 'Non-Trucking Liability', value: 'Yes' });
  }

  const effectiveDate = application?.effectiveDateRequested
    ? application.effectiveDateRequested instanceof Date
      ? application.effectiveDateRequested
      : toDate(String(application.effectiveDateRequested))
    : null;

  return (
    <View style={[styles.container, { width }]}>
      <View style={styles.header}>
        <Typography variant="h4" style={styles.headerText}>
          Summary
        </Typography>
      </View>

      <ScrollView showsVerticalScrollIndicator={false}>
        {/* Company Info */}
        {company && company.id > 0 && (
          <View style={styles.section}>
            <Typography variant="caption1Medium" style={styles.sectionLabel}>
              COMPANY
            </Typography>
            <View style={styles.companyCard}>
              <Typography variant="title3" style={styles.companyName} numberOfLines={2}>
                {company.companyName || 'N/A'}
              </Typography>
              <View style={styles.badgeRow}>
                {company.dotNumber && (
                  <View style={styles.infoBadge}>
                    <Typography variant="caption2" style={styles.badgeLabel}>
                      DOT
                    </Typography>
                    <Typography variant="caption1Medium" style={styles.badgeValue}>
                      {company.dotNumber}
                    </Typography>
                  </View>
                )}
                {company.mcNumber && (
                  <View style={styles.infoBadge}>
                    <Typography variant="caption2" style={styles.badgeLabel}>
                      MC
                    </Typography>
                    <Typography variant="caption1Medium" style={styles.badgeValue}>
                      {company.mcNumber}
                    </Typography>
                  </View>
                )}
              </View>
            </View>
          </View>
        )}

        {/* Application Details */}
        {(coverages.length > 0 || effectiveDate) && (
          <View style={styles.section}>
            <Typography variant="caption1Medium" style={styles.sectionLabel}>
              COVERAGES
            </Typography>
            {coverages.map((c) => (
              <View key={c.label} style={styles.coverageRowEnhanced}>
                <View style={styles.coverageLabelRow}>
                  <Typography variant="caption1Medium" style={styles.checkIcon}>
                    {'\u2713'}
                  </Typography>
                  <Typography variant="caption2" style={styles.coverageLabel} numberOfLines={1}>
                    {c.label}
                  </Typography>
                </View>
                {c.value ? (
                  <View style={styles.coverageBadge}>
                    <Typography variant="caption1Medium" style={styles.coverageBadgeText}>
                      {c.value}
                    </Typography>
                  </View>
                ) : null}
              </View>
            ))}
            {effectiveDate && (
              <View style={styles.coverageRowEnhanced}>
                <Typography variant="caption2" style={styles.coverageLabel}>
                  Eff. Date
                </Typography>
                <View style={styles.dateBadge}>
                  <Typography variant="caption1Medium" style={styles.dateBadgeText}>
                    {formatDate(effectiveDate, 'MM/DD/YYYY')}
                  </Typography>
                </View>
              </View>
            )}
          </View>
        )}

        {/* Progress */}
        <View style={styles.section}>
          <Typography variant="caption1Medium" style={styles.sectionLabel}>
            PROGRESS
          </Typography>
          <View style={styles.progressBarOuter}>
            <View style={[styles.progressBarInner, { width: `${progressPercent}%` }]} />
          </View>
          <Typography variant="caption2" style={styles.progressText}>
            {progressPercent}% complete
          </Typography>
        </View>

        {/* Notes (producer only) */}
        {showProducerTools && (
          <View style={styles.section}>
            <Typography variant="caption1Medium" style={styles.sectionLabel}>
              NOTES
            </Typography>
            <TextInput
              style={styles.notesInput}
              placeholder="Add notes..."
              placeholderTextColor={themes.light.gray[5]}
              multiline
              value={notes}
              onChangeText={setNotes}
              textAlignVertical="top"
            />
            <TouchableOpacity
              style={[styles.saveButton, !isDirty && styles.saveButtonDisabled]}
              onPress={handleSaveNotes}
              disabled={!isDirty || saveStatus === 'saving'}>
              <Typography variant="caption1Medium" style={styles.saveButtonText}>
                {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved!' : 'Save'}
              </Typography>
            </TouchableOpacity>
          </View>
        )}

        {/* Carriers (producer only) */}
        {showProducerTools && (
          <View style={styles.section}>
            <Typography variant="caption1Medium" style={styles.sectionLabel}>
              CARRIERS
            </Typography>
            {carrierError && (
              <Typography variant="caption2" style={styles.detailText}>
                Failed to load carriers.
              </Typography>
            )}
            {!carrierError && carriers && carriers.length > 0
              ? carriers
                  .filter((c) => c.carrierId && c.carrierName)
                  .map((c) => {
                    const rankStyle = getRankBadgeStyle(c.rankLabel || '');
                    return (
                      <View key={c.carrierId} style={styles.carrierCard}>
                        <Typography
                          variant="caption1Medium"
                          style={styles.carrierName}
                          numberOfLines={2}>
                          {c.carrierName}
                        </Typography>
                        {c.rankLabel && (
                          <View style={rankStyle.badge}>
                            <Typography variant="caption2" style={rankStyle.text}>
                              {c.rankLabel}
                            </Typography>
                          </View>
                        )}
                      </View>
                    );
                  })
              : !carrierError && (
                  <Typography variant="caption2" style={styles.detailText}>
                    No carriers available.
                  </Typography>
                )}
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: themes.light.white[1],
    borderLeftWidth: 1,
    borderLeftColor: themes.light.gray[7],
    paddingTop: 16,
  },
  header: {
    paddingHorizontal: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: themes.light.gray[7],
    marginBottom: 4,
  },
  headerText: {
    color: themes.light.gray_blue[1],
  },
  section: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: themes.light.gray[8],
  },
  sectionLabel: {
    color: themes.light.gray[5],
    marginBottom: 4,
    letterSpacing: 0.5,
  },
  detailText: {
    color: themes.light.gray[5],
    marginTop: 2,
  },
  companyCard: {
    backgroundColor: themes.light.brand_blue[7],
    borderRadius: 8,
    padding: 12,
    marginTop: 4,
  },
  companyName: {
    color: themes.light.gray_blue[1],
    marginBottom: 8,
  },
  badgeRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  infoBadge: {
    backgroundColor: themes.light.gray_blue[7],
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  badgeLabel: {
    color: themes.light.gray[5],
  },
  badgeValue: {
    color: themes.light.gray_blue[1],
  },
  coverageRowEnhanced: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
  },
  coverageLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  checkIcon: {
    color: themes.light.green[3],
    marginRight: 6,
  },
  coverageLabel: {
    color: themes.light.gray[5],
  },
  coverageBadge: {
    backgroundColor: themes.light.green[5],
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  coverageBadgeText: {
    color: themes.light.green[2],
  },
  dateBadge: {
    backgroundColor: themes.light.brand_blue[7],
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  dateBadgeText: {
    color: themes.light.brand_blue[3],
  },
  progressBarOuter: {
    height: 6,
    backgroundColor: themes.light.gray[7],
    borderRadius: 3,
    marginTop: 8,
    marginBottom: 6,
    overflow: 'hidden',
  },
  progressBarInner: {
    height: '100%',
    backgroundColor: themes.light.brand_blue[3],
    borderRadius: 3,
  },
  progressText: {
    color: themes.light.gray[5],
  },
  carrierCard: {
    backgroundColor: themes.light.gray_blue[7],
    borderRadius: 8,
    padding: 10,
    marginBottom: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  carrierName: {
    color: themes.light.gray_blue[1],
    flex: 1,
    marginRight: 8,
  },
  rankBadgeGreen: {
    backgroundColor: themes.light.green[5],
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  rankBadgeBlue: {
    backgroundColor: themes.light.brand_blue[7],
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  rankBadgeDefault: {
    backgroundColor: themes.light.gray[8],
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  notesInput: {
    borderWidth: 1,
    borderColor: themes.light.gray_blue[6],
    borderRadius: 6,
    padding: 8,
    minHeight: 80,
    maxHeight: 120,
    fontSize: 13,
    color: themes.light.gray_blue[1],
    backgroundColor: themes.light.white[1],
  },
  saveButton: {
    backgroundColor: themes.light.brand_blue[3],
    borderRadius: 6,
    paddingVertical: 8,
    alignItems: 'center',
    marginTop: 8,
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    color: themes.light.white[1],
  },
});

export default SummaryPanel;
